import { NgSelectComponent } from '@ng-select/ng-select';
import { SidenavService } from '@shared/services/sidenav/sidenav.service';
import { HttpError } from '@shared/interfaces/error.interface';
import { ButtonTypes, ButtonSize } from '@shared/modules/ui/components/button/button.component';
import { AttachmentManagerListComponent } from '@shared/modules/attachment-manage/components/attachment-manager-list/attachment-manager-list.component';
import { SnackBarService } from '@core/services/snackbar.service';
import { Config } from '@shared/configs/config';
import { Contact } from '@modules/contacts/shared/models/contact.model';
import { TranslateService } from '@ngx-translate/core';
import { SIDENAV_DATA } from '@shared/services/sidenav/sidenav.data';
import { MailingHttpService } from '@shared/modules/mailings/shared/services/mailing-http.service';
import { finalize, tap, switchMap } from 'rxjs/operators';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewChild,
  Inject,
  inject,
  NgZone,
  AfterViewInit,
  DestroyRef
} from '@angular/core';
import { TextAttachmentsConfig } from '@shared/components/text-attachments-input/models/text-attachments-config';
import { Regex } from '@shared/configs/regex';
import { MailingQuillFormats } from './data/mailing-quill-formats.data';
import { MailingQuillModules } from './data/mailing-quill-modules.data';
import { Subscription, of } from 'rxjs';
import { MailingForm } from '@shared/modules/mailings/shared/enums/mailing-form.enum';
import { MailingTemplate } from '../../models/mailing-template.model';
import { EMailingStates, MailingService } from '@shared/modules/mailings/shared/services/mailing.service';
import { addStyleToParagraphs } from '@shared/helpers/add-styles.herlper';
import { ProjectCreatorEntityDiscardComponent } from '@modules/project-creator/shared/modules/project-creator-entity-sidenav/components/project-creator-entity-discard/project-creator-entity-discard.component';
import { ActivatedRoute } from '@angular/router';
import { IntilioCodes } from '@shared/enums/initilio-codes.enum';
import { MatTooltip } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { ImageAttachmentModalComponent } from '@shared/components/image-attachment-modal/image-attachment-modal.component';
import { AttachmentService } from '@shared/services/attachment.service';
import { AttachmentType } from '@shared/components/text-attachments-input/enums/text-attachment-type.enum';
import { getFileType } from '@shared/helpers/basic-file-type.helper';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-mailing-sidenav',
  templateUrl: './mailing-sidenav.component.html',
  styleUrls: ['./mailing-sidenav.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MailingSidenavComponent implements OnInit, AfterViewInit {
  readonly cssStyles = {
    p: 'padding:0;margin:0;',
    ul: 'padding-top:0;padding-bottom:0;margin-top:0;margin-bottom:0;',
    ol: 'padding-top:0;padding-bottom:0;margin-top:0;margin-bottom:0;',
    blockquote:
      'border-left: 4px solid #ccc;margin-bottom: 5px;margin-top: 5px;padding-left: 16px;margin-left:0;'
  };

  private zone = inject(NgZone);
  private attachmentService = inject(AttachmentService);
  private readonly destroyRef = inject(DestroyRef);

  static componentName: string = 'MailingSidenavComponent';
  clipboardFileName: string;
  projectId: number;
  changesSummary: any;

  readonly MailingForm = MailingForm;
  readonly ButtonSize = ButtonSize;
  readonly ButtonTypes = ButtonTypes;

  @ViewChild('tooltip') tooltip: MatTooltip;
  @ViewChild('attachmentsList') attachmentsList: AttachmentManagerListComponent;
  @ViewChild('editor') editor;
  @ViewChild('participants') participants: NgSelectComponent;

  form: UntypedFormGroup;
  sendingTemplate: boolean = false;
  loadingTemplate: boolean = false;
  initialEmails: any[] = [];
  firstLoad: boolean = false;

  sub: Subscription = new Subscription();

  attachmentsConfig: TextAttachmentsConfig = {
    fileAttachments: true,
    photoAttachments: true,
    videoAttachments: true,
    maxAttachmentSize: 50
  };

  template: MailingTemplate;
  modules = {
    ...MailingQuillModules(),
    imageHandler: {
      upload: (file) => {
        return new Promise((resolve, reject) => {
          this.zone.run(() => {
            this.dialog
              .open(ImageAttachmentModalComponent, { width: Config.CONFIRM_MODAL_WIDTH })
              .afterClosed()
              .pipe(
                tap((data) => {
                  if ((data?.asAttachment && !data?.asImage) || !data) this.clearImageFromQuillAtIndex();
                  if (data?.asAttachment) this.onAttachmentAdded(file);
                  if (data?.asImage) return this.uploadFile(file, { resolve, reject });
                })
              )
              .subscribe();
          });
        });
      },
      accepts: ['*']
    },
    imageDropAndPaste: {
      handler: this.uploadImage.bind(this)
    }
  };
  formats: string[] = MailingQuillFormats;

  get formData() {
    const formValue = Object.assign({}, this.form.value);
    formValue.emails = formValue[MailingForm.RECIEVER];
    formValue.projectId = formValue[MailingForm.PROJECT];
    formValue.attachmentPacketId = this.attachmentsList.service.attachmentPacket?.id || null;

    delete formValue[MailingForm.PROJECT];
    delete formValue[MailingForm.RECIEVER];
    delete formValue[MailingForm.ATTACHMENTS];
    return formValue;
  }

  get recieverIds() {
    return this.sideData.data?.recieverIds ?? [];
  }

  get ContactsUrl() {
    const ids = this.recieverIds?.map((id: number) => `requiredIds[]=${id}`).join('&');
    return `${Config.API}/contact/search?emailsOnly=1${ids ? '&' + ids : ''}`;
  }

  get formattedText() {
    return this.form.controls[MailingForm.DESCRIPTION].value;
  }

  get errorMessages() {
    return Config.validationMessages;
  }

  get isDigitalChanges(): boolean {
    return !!(this.template?.number === 25);
  }

  get taskIds(): number[] {
    return this.sideData?.data?.taskIds || [];
  }

  constructor(
    @Inject(SIDENAV_DATA) public sideData: SIDENAV_DATA,
    private fb: UntypedFormBuilder,
    private changes: ChangeDetectorRef,
    private http: MailingHttpService,
    private t: TranslateService,
    private s: SnackBarService,
    private sideService: SidenavService,
    private mailingService: MailingService,
    private dialog: MatDialog,
    private active: ActivatedRoute
  ) {}

  ngOnInit() {
    this._createForm();
    this._setInitialData();
    this._quillChangeDetector();
    this._subscribeProjectChange();
  }

  ngAfterViewInit(): void {
    this.initEditorCustomMatcher();
  }

  private _quillChangeDetector(): void {
    this.form
      .get(MailingForm.DESCRIPTION)
      .valueChanges.pipe(
        tap(() => this.changes?.detectChanges()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private _subscribeProjectChange(): void {
    this.form
      .get(MailingForm.PROJECT)
      .valueChanges.pipe(
        tap((project) => {
          this.projectId = project;
          this._getTemplate();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this.form.get(MailingForm.PROJECT).updateValueAndValidity();
  }

  isControlInvalid(name: string) {
    return this.form?.controls[name]?.touched && this.form?.controls[name]?.invalid;
  }

  private _setInitialData() {
    this.projectId = Number(this.sideData?.data?.projectId) || null;
    this.form.get(MailingForm.PROJECT).setValue(this.projectId);
  }

  private _getTemplate() {
    this.loadingTemplate = true;
    this.changes.detectChanges();

    const id = this.sideData?.data?.templateId;
    const modalData = JSON.parse(this.active?.snapshot?.queryParams['modalData']);

    this.http
      .getEmailTemplateById(id, this.projectId, modalData?.templateId === 25)
      .pipe(tap((template) => (this.template = template)))
      .pipe(tap(() => this._fillTemplate(this.template)))
      .pipe(
        switchMap(() =>
          this.isDigitalChanges
            ? this.http
                .getProjectChanges(this.projectId, this.taskIds)
                .pipe(tap((changesSummary) => (this.changesSummary = changesSummary)))
            : of({})
        )
      )
      .pipe(
        finalize(() => {
          this.loadingTemplate = false;
          this.changes.detectChanges();
        })
      )
      .subscribe();
  }

  private reloadTemplateFactory() {
    if (!this.firstLoad) {
      this.firstLoad = true;
      const email = this.sideData?.data?.email || null;
      if (email) {
        this.initialEmails = [this.createEmailTag(email)];
        const _email = this.isDigitalChanges ? email : [email];
        setTimeout(() => this.form.get(MailingForm.RECIEVER).setValue(_email), 0);
      }

      if (!!this.recieverIds?.length) {
        setTimeout(() => {
          const items = this.participants?.itemsList?.items?.filter((i: any) => {
            return this.recieverIds?.indexOf(i?.value?.id) > -1;
          });
          console.log(items, this.recieverIds);
          this.form
            .get(MailingForm.RECIEVER)
            .setValue(items.map((i: any) => i?.value?.email).filter(Boolean));
        }, 100);
      }
    }

    const content = this.template.content;
    const separator = '[Podgląd zmian]';
    const header = content.split(separator)[0] || null;
    const footer = content.split(separator)[1] || null;

    this.form.get(MailingForm.DESCRIPTION).setValue(header);
    this.form.get(MailingForm.DESCRIPTION_END).setValue(footer);
  }

  private _createForm() {
    this.form = this.fb.group({
      [MailingForm.PROJECT]: [this.projectId],
      [MailingForm.NAME]: [null],
      [MailingForm.RECIEVER]: [null, [Validators.required]],
      [MailingForm.TITLE]: [null, [Validators.required]],
      [MailingForm.DESCRIPTION]: [null, [Validators.required]],
      [MailingForm.DESCRIPTION_END]: [null],
      [MailingForm.ATTACHMENTS]: []
    });
  }

  private _fillTemplate(template) {
    this.form.get(MailingForm.NAME).setValue(template.name);
    this.form.get(MailingForm.TITLE).setValue(template.title);

    const controls = [MailingForm.NAME, MailingForm.TITLE, MailingForm.DESCRIPTION];
    controls.forEach((controlName: string) => this.form.get(controlName).markAsPristine());
    this.reloadTemplateFactory();
  }

  showTooltip() {
    setTimeout(() => {
      this.tooltip.show();
      setTimeout(() => this.tooltip.hide(), 600);
    }, 0);
  }

  validateEmail($event) {
    if (!!$event?.length && !Regex.email.test($event.toString())) {
      this.s.error(this.t.instant('FormErrors.email'));
      return null;
    }
    const name = $event;
    return this.createEmailTag(name);
  }

  createEmailTag(name: string) {
    return { id: name, name, newItem: true, fullName: name, email: name };
  }

  successOnLoadContacts({ records }: { records: Contact[] }) {
    return [...records.map((c) => new Contact(c))];
  }

  changePromiseFunc($event) {
    const controls = [MailingForm.NAME, MailingForm.TITLE, MailingForm.DESCRIPTION];
    const pristine = controls.filter((controlName: string) => !!this.form.get(controlName).pristine);

    return controls.length !== pristine.length
      ? confirm(this.t.instant('Mailings.Templates.confirmation'))
      : true;
  }

  filesUploaded($event) {
    console.log(this.attachmentsList);
  }

  discardSaving() {
    if (this.form.pristine) {
      this.closeSidenav();
    } else {
      this.dialog.open(ProjectCreatorEntityDiscardComponent, {
        width: Config.DEFAULT_MODAL_WIDTH,
        autoFocus: false,
        data: {
          confirm: this.closeSidenav.bind(this)
        }
      });
    }
  }

  closeSidenav() {
    this.sideService.close();
  }

  submit() {
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();
    this.changes.detectChanges();
    if (this.form.invalid) return;

    if (!this.isDigitalChanges) {
      this.sendTemplate();
    } else {
      this.sendChanges();
    }
  }

  sendChanges() {
    const contentHeader = addStyleToParagraphs(
      this.form.get(MailingForm.DESCRIPTION).value || ' ',
      this.cssStyles
    );
    const contentFooter = addStyleToParagraphs(
      this.form.get(MailingForm.DESCRIPTION_END).value || ' ',
      this.cssStyles
    );

    const formValue = {
      email: this.form.get(MailingForm.RECIEVER).value,
      title: this.form.get(MailingForm.TITLE).value,
      contentHeader: contentHeader,
      contentFooter: contentFooter,
      tasksIds: this.taskIds, //get from data
      projectId: this.projectId, // get from data
      attachmentPacketId: this.attachmentsList.service.attachmentPacket?.id || null
    };

    this.sendingTemplate = true;
    this.changes.detectChanges();
    this.http
      .shareChange(formValue)
      .pipe(
        finalize(() => {
          this.sendingTemplate = false;
          this.changes.detectChanges();
          this.mailingService.mailingSubject.emit({ action: EMailingStates.EMAIL_SENT });
        })
      )
      .subscribe({
        next: () => this.successSendingMsg(),
        error: (err: HttpError) => this.errorSendingMsg(err)
      });
  }

  sendTemplate() {
    this.sendingTemplate = true;
    this.changes.detectChanges();
    const formData = this.formData;
    const normalized = this.normalizeEmptyParagraphs(formData.content);
    formData.content = addStyleToParagraphs(normalized, this.cssStyles);
    this.http
      .sendProjectStageEmail(formData)
      .pipe(
        finalize(() => {
          this.sendingTemplate = false;
          this.changes.detectChanges();
        })
      )
      .subscribe({
        next: () => this.successSendingMsg(),
        error: (err: HttpError) => this.errorSendingMsg(err)
      });
  }

  normalizeEmptyParagraphs(html: string): string {
    return html.replace(/<p><\/p>/g, '<p><br></p>');
  }

  successSendingMsg() {
    this.sideService.close();
    this.s.success(this.t.instant('Mailings.Templates.successMessageSending'));
  }

  errorSendingMsg(err: HttpError) {
    switch (err.messageCode) {
      case IntilioCodes.GOOGLE_AUTH_EXPIRED:
        this.s.error(this.t.instant('Mailings.Templates.googleAuthExpired'));
        break;
      default:
        this.s.error(this.t.instant('Mailings.Templates.errorMessageSending'));
        break;
    }
  }

  uploadImage(imageDataUrl, type, imageData) {
    const quill = this.editor.quillEditor;
    const selection = quill.getSelection();
    const insertIndex = selection ? selection.index : quill.getLength();

    this.clearImageFromQuillAtIndex();

    this.zone.run(() => {
      this.dialog
        .open(ImageAttachmentModalComponent, { width: Config.CONFIRM_MODAL_WIDTH })
        .afterClosed()
        .subscribe((data) => {
          const file = imageData.toFile(this.clipboardFileName || imageData.name);

          if (data?.asAttachment) this.onAttachmentAdded(file);

          if (data?.asImage) {
            this.uploadFile(file, {
              resolve: (url) => {
                quill.insertEmbed(insertIndex - 1, 'image', url, 'user');
                setTimeout(() => quill.setSelection(insertIndex, 0));
              }
            });
          }

          this.clipboardFileName = '';
        });
    });
  }

  uploadFile(
    file: File,
    { resolve, reject }: { resolve?: (url: string) => void; reject?: (reason?: any) => void } = {}
  ): void {
    if (getFileType(file) === AttachmentType.FILE) {
      this.s.error(this.t.instant('ProjectCreator.imagesAllowed'));
      reject?.('Unsupported');
      return;
    }

    this.attachmentService
      .uploadAttachment({
        objectId: `id-${file.name}`,
        objectType: 'wysiwyg-content',
        files: [file]
      })
      .subscribe({
        next: ([data]) => {
          const url = `${Config.PRIVATE_ATTACHMENT}${data.file}`;
          resolve?.(url);
        },
        error: (err) => {
          console.error('File upload failed:', err);
          reject?.('Upload failed');
        }
      });
  }

  onAttachmentAdded(attachment): void {
    this.attachmentsList.onFilesChange([attachment]);
  }

  clearImageFromQuillAtIndex(): void {
    const quill = this.editor.quillEditor;
    const selection = quill.getSelection();
    const index = selection ? selection.index : quill.getLength() - 1;

    const [blot] = quill.getLeaf(index);
    if (!blot) return;

    const node = blot.domNode as HTMLElement;

    if (node.tagName === 'IMG' && node.getAttribute('src')?.startsWith('data:')) {
      quill.deleteText(index - 1, 1, 'user');
    }

    const progressBarEl = quill.container.parentElement?.querySelector('.quill-progress-wrapper');
    progressBarEl?.remove();
  }

  initEditorCustomMatcher(): void {
    document.querySelector('.quill-custom-matcher').addEventListener('paste', (e: ClipboardEvent) => {
      const clipboardData = e.clipboardData || (window as any).clipboardData;
      const isImage = clipboardData.types.length && clipboardData.types.includes('Files');
      if (!isImage) return;
      const file = clipboardData.files[0];
      this.clipboardFileName = file.name;
    });
  }
}
